import { useEffect, useRef, useState } from "react";

const VIDEO_SRC =
  "https://customer-assets-v7afamib.emergentagent.net/job_fe0c775c-382e-4bf1-ab8e-bb795dea130b/artifacts/x7hbgxyw_gemini_generated_video_D3537672.MP4";

/**
 * FullVideo — the MP4 sits truly `position: fixed` behind the page while its
 * spacer section is in the viewport. Sections above and below (which have
 * their own opaque backgrounds) roll over the video as the user scrolls, so
 * the video never moves — the page moves over it until it's gone.
 *
 * We only mount/show the fixed layer while the spacer is on screen, so the
 * video doesn't bleed behind unrelated sections or waste GPU when off-screen.
 */
export const FullVideo = () => {
  const spacerRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = spacerRef.current;
    if (!node) return;
    const io = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0, rootMargin: "0px" }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  return (
    <>
      {/* Truly fixed backdrop — only rendered while the spacer is in view. */}
      {visible && (
        <div
          aria-hidden
          data-testid="full-video-fixed-layer"
          className="pointer-events-none fixed inset-0 z-0 h-screen min-h-[100svh] w-full overflow-hidden"
        >
          <video
            data-testid="full-video-player"
            src={VIDEO_SRC}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/15 via-transparent to-black/25" />
        </div>
      )}

      {/* Transparent spacer — this is the "window" through which the fixed
          video is visible. Height controls how long the video stays on
          screen before the next section rolls over it. */}
      <section
        ref={spacerRef}
        id="full-video"
        data-testid="full-video-section"
        className="relative h-screen min-h-[100svh] w-full bg-transparent"
      />
    </>
  );
};
